import { BaseSyntheticEvent } from 'react';
import { Form as BSForm, FormProps as BSFormProps } from 'react-bootstrap';
import {
  FieldValues,
  FormProvider as RHFProvider,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormContextParts, FormDirtyCheckerOptions } from './Form.types';
import FormDirtyChecker from './FormDirtyChecker';

interface Props<FormValues> {
  defaultValues?: { [key: string]: any };
  onSubmit: (
    data: FormValues,
    event: BaseSyntheticEvent | undefined,
    // @ts-expect-error TS2344 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
    formContextParts: FormContextParts<FormValues>,
  ) => void;
  onInvalid?: (_: any) => void;
  schema?: { [key: string]: any };
  resolver?: any;
  children: any;
  disabled?: boolean;
  [key: string]: any;
}

type FormProps = Omit<BSFormProps, 'onSubmit'>;

type FormWithProviderProps<FormValues> = Props<FormValues> &
  FormProps & {
    dirtyChecker?: boolean;
    dirtyCheckerOptions?: FormDirtyCheckerOptions;
  };

export const FormProvider = <FormValues extends FieldValues>({
  defaultValues,
  disabled,
  children,
  schema,
  resolver,
}: Omit<Props<FormValues>, 'onSubmit' | 'onInvalid'>) => {
  const methods = useForm({
    defaultValues,
    disabled,
    resolver: resolver
      ? yupResolver(resolver)
      : yupResolver(Yup.object(schema).required()),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    shouldFocusError: true,
  });

  return <RHFProvider {...methods}>{children}</RHFProvider>;
};

export const Form = <FormValues extends FieldValues>({
  children,
  onSubmit,
  onInvalid,
  ...props
}: Pick<Props<FormValues>, 'children' | 'onSubmit' | 'onInvalid'> &
  FormProps) => {
  const {
    // form state needs to be deconstructed, otherwise its performance optimisations may result in not having the latest changes propagated in e.g. dirtyFields (ref: https://www.react-hook-form.com/api/useformstate/)
    formState: { defaultValues, dirtyFields, errors },
    handleSubmit,
    reset,
    setError,
  } = useFormContext<FormValues>();

  const submitHandler = (
    data: FormValues,
    event: BaseSyntheticEvent | undefined,
  ) => {
    const formState = { defaultValues, dirtyFields, errors };
    return onSubmit(data, event, { formState, reset, setError }); // must return in order to be able to await for async submits
  };

  return (
    <BSForm onSubmit={handleSubmit(submitHandler, onInvalid)} {...props}>
      {children}
    </BSForm>
  );
};

/**
 * This component is the default Form that should be used in the app.
 * It wraps the react-hook-form FormProvider and adds a validation schema as a prop.
 * Any new modifications in the UI and validation should be done here.
 */
const FormWithProvider = <FormValues extends FieldValues>({
  defaultValues,
  dirtyChecker,
  dirtyCheckerOptions,
  disabled,
  children,
  onSubmit,
  onInvalid,
  schema,
  resolver,
  ...rest
}: FormWithProviderProps<FormValues>) => {
  return (
    <FormProvider
      defaultValues={defaultValues}
      disabled={disabled}
      schema={schema}
      resolver={resolver}
    >
      <Form onSubmit={onSubmit} onInvalid={onInvalid} {...rest}>
        {dirtyChecker && <FormDirtyChecker options={dirtyCheckerOptions} />}
        {children}
      </Form>
    </FormProvider>
  );
};

FormWithProvider.defaultProps = {
  defaultValues: {},
  dirtyChecker: false,
  dirtyCheckerOptions: undefined,
  disabled: false,
  resolver: null,
  schema: {},
  onInvalid: () => {},
};

FormProvider.defaultProps = {
  defaultValues: {},
  disabled: false,
  resolver: null,
  schema: {},
};

export default FormWithProvider;
